import { partnerBrandingList } from 'Api/spec/partner-branding';
import { loadPortalSession } from 'helpers/portalSession';
import { FALLBACK_BRANDING } from './brandTokens';

/**
 * WHERE THE PORTAL'S BRANDING COMES FROM.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * §30.1 Get Branding — POST /app/v1/partners/branding/view, body
 * `{ partnerCode }`. One call, once per tab, in the background, off the
 * critical path. Everything else here exists to keep it that way.
 *
 * ── THE DEFAULT IS ZENVOYA'S HOUSE PALETTE ──────────────────────────────────
 * When the API returns nothing, errors, or has never been saved for a partner,
 * the console paints Zenvoya's own colours — #6C00F6 / #A1F919 / #00B8FF.
 * That is `FALLBACK_BRANDING`, and
 * `withBrandingFallbacks` fills each missing or malformed field from it
 * INDIVIDUALLY. So a partial response — and §30.1 returns nulls for every
 * unset field before a partner's first save — can never blank part of the
 * theme; each field independently falls back to the shipped value.
 *
 * ── PERFORMANCE: WHAT THIS COSTS ────────────────────────────────────────────
 * Nothing on first paint, and one request per tab.
 *
 *  1. BOOT IS SYNCHRONOUS AND OFFLINE. `getBootBranding()` reads a
 *     sessionStorage cache and returns immediately, so the theme store can
 *     apply the brand at module load — before React's first render. No fetch
 *     blocks the shell and there is no default-then-brand flash to repaint.
 *  2. ONE FETCH PER TAB. `loadPortalBranding()` de-duplicates through a
 *     module-level promise, so StrictMode's double mount, a re-render, or two
 *     components asking at once all share a single in-flight request. It is
 *     never called per route or on a timer.
 *  3. THE FETCH IS FIRE-AND-FORGET. It resolves after the shell has painted and
 *     only writes state if the brand actually differs (see `sameBranding`), so
 *     the common "nothing changed since last load" case costs zero re-renders.
 *  4. THE CACHE IS KEYED BY partnerCode, so signing into a different partner in
 *     the same tab cannot paint the previous partner's colours.
 */

/** No partner branding available -> the console looks exactly as it always has. */
export const DEFAULT_PORTAL_BRANDING = { ...FALLBACK_BRANDING };

/**
 * ══ DEMO BRANDS — offline override, normally OFF ════════════════════════════
 *
 * Set `ACTIVE_DEMO_BRAND` to a key below and the whole console repaints from it
 * with NO network call at all, which is how the plumbing was proved before the
 * endpoint existed. It also WINS OVER THE API, so leave it '' for real use —
 * otherwise a partner's saved brand is invisible.
 */
export const DEMO_BRANDS = {
  /** The Primary / Secondary / Accent from the Branding & Theming screenshot. */
  zenvoya: {
    name: 'Zenvoya',
    primaryColor: '#6C00F6',
    secondaryColor: '#A1F919',
    accentColor: '#00B8FF',
    fontFamily: 'Inter',
    logoUrl: '',
    logoDarkUrl: '',
    iconUrl: '',
    faviconUrl: '',
  },
  /** Nothing purple survives — for confirming every surface follows the brand. */
  verdant: {
    name: 'Verdant Escapes',
    primaryColor: '#1B5E3F',
    secondaryColor: '#C9A227',
    accentColor: '#6FA88A',
    fontFamily: 'Inter',
    logoUrl: '',
    logoDarkUrl: '',
    iconUrl: '',
    faviconUrl: '',
  },
};

/** '' -> use the API. A key of DEMO_BRANDS -> force that brand, no network. */
export const ACTIVE_DEMO_BRAND = '';

/**
 * Is a demo brand forcing the theme?
 *
 * `useThemeStore.setPartner` asks, because the legacy `partnerThemes` map still
 * carries hardcoded colours and would otherwise overwrite the theme on login.
 */
export const isDemoBrandingActive = () => Boolean(DEMO_BRANDS[ACTIVE_DEMO_BRAND]);

/* ── §30.1 wire -> branding ─────────────────────────────────────────────── */

/**
 * Map the §30.1 response onto the shape the theme consumes.
 *
 * THREE THINGS THE WIRE DOES THAT THIS ABSORBS, all of which have bitten:
 *  - The ACCENT has three names. The server persists `accentColor` and
 *    `headerBackgroundColor`; the branding page's form buffer calls it
 *    `headerBg`. All three are accepted.
 *  - LOGOS ARRIVE TWO WAYS. Either nested (`logos.primary.url`) or as flat URL
 *    fields (`logoUrl`), and the flat ones can be the literal STRING "null"
 *    rather than a real null — which would otherwise be used as an image src
 *    and render a broken icon.
 *  - EVERY FIELD CAN BE null before a partner's first save. Nulls are dropped
 *    rather than passed through, so `withBrandingFallbacks` sees "absent" and
 *    applies the default instead of writing `null` into the theme.
 */
export const brandingFromWire = (payload) => {
  // Top-level fields first. §30.1 now always carries `theme` — the theme
  // studio's overrides, `{}` when none — and reading from it alone missed the
  // top-level `logoUrl`, so every refresh blanked the sidebar logo.
  const src = { ...(payload?.theme ?? {}) };
  Object.entries(payload ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined) src[key] = value;
  });
  const out = {};
  const set = (key, value) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'null') out[key] = value;
  };

  set('name', src.name ?? src.partnerName);
  set('primaryColor', src.primaryColor);
  set('secondaryColor', src.secondaryColor);
  set('accentColor', src.accentColor ?? src.headerBg ?? src.headerBackgroundColor);
  set('fontFamily', src.fontFamily);

  // Nested asset objects win over the flat URL fields — that is the order
  // `logosFrom` on the branding page uses, so both read one upload the same way.
  const logos = payload?.logos ?? {};
  set('logoUrl', logos.primary?.url ?? src.logoUrl);
  set('logoDarkUrl', logos.dark?.url ?? src.logoDarkUrl);
  set('iconUrl', logos.icon?.url ?? src.iconUrl);
  set('faviconUrl', logos.favicon?.url ?? src.faviconUrl);

  return out;
};

/* ── Cache: what makes the first paint free ─────────────────────────────── */

const CACHE_KEY = '_zenvoya_partner_branding';

/**
 * The signed-in partner, read straight from the persisted session.
 *
 * Synchronous on purpose: a reload rehydrates the session from sessionStorage
 * before React mounts, so the cache can be keyed correctly at module load.
 * Returns '' when signed out, which is when there is no branding to fetch.
 */
export const currentPartnerCode = () => {
  try {
    return String(loadPortalSession('partner')?.partner?.partnerCode ?? '');
  } catch {
    return '';
  }
};

const readCache = () => {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Keyed by partner: signing into a different partner in the same tab must
    // not inherit the previous one's colours for a frame.
    if (!parsed?.branding || String(parsed.code ?? '') !== currentPartnerCode()) return null;
    return parsed.branding;
  } catch {
    return null;
  }
};

const writeCache = (branding) => {
  try {
    window.sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ code: currentPartnerCode(), branding })
    );
  } catch {
    /* private mode — the app simply pays one default-then-brand repaint */
  }
};

/** Drop the cached brand — called on sign-out so the next partner starts clean. */
export const clearBrandingCache = () => {
  try {
    window.sessionStorage.removeItem(CACHE_KEY);
  } catch {
    /* nothing to do; a stale cache is re-keyed by partner code anyway */
  }
};

/**
 * Remember a brand the app already has in hand — the §30.2 save response.
 *
 * Saving on the branding page repaints the console immediately through the
 * store; this is what makes that survive a RELOAD without waiting on a fetch.
 *
 * MERGES rather than replaces. The save response carries the THEME only, so
 * writing it flat would drop the logo URLs the §30.1 fetch had cached and the
 * next reload would paint a branded console with no logo until the refetch
 * landed.
 */
export const cachePortalBranding = (branding) =>
  writeCache({ ...(readCache() ?? {}), ...(branding ?? {}) });

/**
 * The branding to render with on the FIRST paint, resolved synchronously.
 *
 * Demo override, else this tab's cached brand, else today's default. Never
 * touches the network — see the performance note at the top.
 */
export const getBootBranding = () =>
  DEMO_BRANDS[ACTIVE_DEMO_BRAND] ?? readCache() ?? DEFAULT_PORTAL_BRANDING;

/* ── The one network call ───────────────────────────────────────────────── */

/**
 * Fetch this partner's branding from §30.1.
 *
 * Returns the mapped branding, or `null` when there is nothing to apply — no
 * session, a failed call, or a response with no usable field. `null` means
 * "leave the theme alone", which is what keeps a 500 or an expired session from
 * flashing the console back to the default.
 */
export const resolvePortalBranding = async () => {
  if (isDemoBrandingActive()) return DEMO_BRANDS[ACTIVE_DEMO_BRAND];

  const partnerCode = currentPartnerCode();
  if (!partnerCode) return null;

  try {
    const result = await partnerBrandingList({ partnerCode });
    if (!result?.ok) {
      /**
       * A DEFINITE "NO" IS AUTHORITATIVE — drop the brand, do not keep it.
       *
       * `null` here would be read by the caller as "leave whatever is on screen
       * alone" (see `hydrateBranding` in useThemeStore). That is right for a
       * flaky network and wrong for this branch, and the difference is visible:
       * revoke a user's branding permission and refresh, and the console kept
       * painting the brand it was no longer allowed to read — from the cache —
       * until a full sign-out.
       *
       * What matters is not the status code, it is whether the server ANSWERED.
       * `ok: false` means it did and the answer was no: permission revoked,
       * partner gone, branding cleared. Every one of those means "stop showing
       * that brand". A thrown request never got an answer, and only that is
       * treated as transient — see the catch below, which still returns null.
       *
       * The cache is cleared too, or the next boot would re-apply the stale
       * brand from it before this call had a chance to say no again.
       */
      clearBrandingCache();
      return DEFAULT_PORTAL_BRANDING;
    }
    /**
     * A LOGO SLOT THE SERVER LEFT OUT IS EMPTY — deleted, or never uploaded.
     *
     * `brandingFromWire` drops null fields so a missing COLOUR falls back to the
     * default. For the logos that same drop was a bug: `setBranding` merges, so
     * an absent `logoUrl` kept the URL from the tab's cache, and a deleted logo
     * came back on reload as a broken image. The server answered, so its silence
     * on a slot is authoritative — clear it. Colours are left to the merge.
     */
    const branding = {
      logoUrl: '', logoDarkUrl: '', iconUrl: '', faviconUrl: '',
      ...brandingFromWire(result.data),
    };
    writeCache(branding);
    return branding;
  } catch {
    // Branding is cosmetic. A network failure must never surface as an error
    // or block the console; the default (or the cache) is already on screen.
    return null;
  }
};

/**
 * De-duplicated fetch — the only entry point the app should call.
 *
 * ONE REQUEST PER TAB. Held in a module-level promise so StrictMode's double
 * mount, a re-render, or two callers at once all share it. Re-armed when the
 * partner changes, so signing into another partner in the same tab refetches.
 */
let inFlight = null;
let loadedForCode = null;

export const loadPortalBranding = () => {
  const code = currentPartnerCode();
  if (inFlight && loadedForCode === code) return inFlight;
  loadedForCode = code;
  inFlight = resolvePortalBranding().finally(() => {
    // Cleared so a later explicit reload (a save, a partner switch) can refetch,
    // while the guard above still collapses the burst this was written for.
    inFlight = null;
  });
  return inFlight;
};

/** Forget that this tab has loaded — used on sign-out and partner switch. */
export const resetBrandingLoad = () => {
  inFlight = null;
  loadedForCode = null;
};
